import type { Decimal, DecimalSource } from '../../math/num';
import { D, mul, pow } from '../../math/num';
import type { DamageType, PortSpec } from '../../graph/types';
import type { AffixStat, AttackKind, PartCategory, Rarity } from './partTypes';

/**
 * Part definitions — content as data. The evaluator interprets each category's
 * numeric params; new parts are data rows, not engine branches. Connectivity
 * grows simply by declaring more modifier ports on rarer defs.
 */

export interface AffixSlotSpec {
  stat: AffixStat;
  weight: number;
  /** magnitude range per rarity (rolled value lerps between min/max). */
  rolls: Partial<Record<Rarity, { min: number; max: number }>>;
}

export interface AttackParams {
  kind: AttackKind;
  damageType: DamageType;
  baseDamage: DecimalSource;
  cooldownMs: number;
  baseCritChance: number;
  baseCritMult: DecimalSource;
  flowScale: DecimalSource; // how much incoming power adds to damage
  maxTargets?: number; // for aoe
}

export interface PartDef {
  id: string;
  category: PartCategory;
  name: string;
  flavor: string;
  baseRarity: Rarity;
  weight: number;
  minTier: number;
  ports: PortSpec[];
  /** drop pool weight; 0 = never drops (starter/built-in only). */
  dropWeight: number;
  attack?: AttackParams;
  source?: { basePower: DecimalSource };
  amplifier?: { multBase: number; multPerLevel: number };
  tuner?: { stat: 'critChance' | 'critMult' | 'power' | 'cooldown'; base: number; perLevel: number };
  utility?: { stat: 'dropRate' | 'scrapMult' | 'componentMult' | 'regen'; base: number; perLevel: number };
  frame?: { weightBonus: number };
  affixSlots: AffixSlotSpec[];
  maxAffixes: Record<Rarity, number>;
}

const ATK_MOD_PORTS = (n: number): PortSpec[] =>
  Array.from({ length: n }, (_, i) => ({ id: `m${i + 1}`, direction: 'in' as const, flow: 'mod' as const, role: 'modifier' as const }));

const attackPorts = (modSlots: number): PortSpec[] => [
  { id: 'in', direction: 'in', flow: 'power', role: 'flow' },
  ...ATK_MOD_PORTS(modSlots),
];

/**
 * Standard upgrade economics: costs grow exponentially while per-level GAINS
 * diminish — each level buys less than the last (level growth follows
 * (L-1)^0.7). This is the anti-snowball: spend can never outrun the curve.
 */
export const LEVEL_GROWTH_EXP = 0.7;
export const levelGrowth = (level: number): number => Math.pow(Math.max(0, level - 1), LEVEL_GROWTH_EXP);
export const upgradeScale = (level: number): Decimal => D(1).add(mul(D(0.2), D(levelGrowth(level))));
export const upgradeCost = (def: PartDef, level: number): Decimal => mul(D(baseUpgrade(def)), pow(D(1.7), level - 1));
function baseUpgrade(def: PartDef): number {
  const byRarity: Record<Rarity, number> = { common: 6, uncommon: 12, rare: 24, epic: 50, legendary: 110, unit: 230, goated: 480 };
  return byRarity[def.baseRarity];
}

const MAX_AFFIX: Record<Rarity, number> = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, unit: 5, goated: 6 };

// Common affix ranges, reused across defs.
const critChanceSlot: AffixSlotSpec = {
  stat: 'critChance',
  weight: 1,
  rolls: { uncommon: { min: 0.02, max: 0.04 }, rare: { min: 0.04, max: 0.07 }, epic: { min: 0.07, max: 0.11 }, legendary: { min: 0.11, max: 0.18 }, unit: { min: 0.18, max: 0.26 }, goated: { min: 0.26, max: 0.36 } },
};
const critMultSlot: AffixSlotSpec = {
  stat: 'critMult',
  weight: 1,
  rolls: { uncommon: { min: 0.15, max: 0.3 }, rare: { min: 0.3, max: 0.55 }, epic: { min: 0.55, max: 0.9 }, legendary: { min: 0.9, max: 1.5 }, unit: { min: 1.5, max: 2.2 }, goated: { min: 2.2, max: 3.2 } },
};
const powerSlot: AffixSlotSpec = {
  stat: 'power',
  weight: 1.2,
  rolls: { uncommon: { min: 0.08, max: 0.16 }, rare: { min: 0.16, max: 0.3 }, epic: { min: 0.3, max: 0.5 }, legendary: { min: 0.5, max: 0.85 }, unit: { min: 0.85, max: 1.3 }, goated: { min: 1.3, max: 1.9 } },
};
const cooldownSlot: AffixSlotSpec = {
  stat: 'cooldown',
  weight: 0.8,
  // Modest rolls: total cooldown reduction hard-caps at 20% (CD_RED_CAP).
  rolls: { uncommon: { min: 0.02, max: 0.04 }, rare: { min: 0.04, max: 0.06 }, epic: { min: 0.06, max: 0.09 }, legendary: { min: 0.09, max: 0.14 }, unit: { min: 0.14, max: 0.17 }, goated: { min: 0.17, max: 0.2 } },
};
const dropSlot: AffixSlotSpec = {
  stat: 'dropRate',
  weight: 0.6,
  rolls: { uncommon: { min: 0.05, max: 0.1 }, rare: { min: 0.1, max: 0.2 }, epic: { min: 0.2, max: 0.35 }, legendary: { min: 0.35, max: 0.6 }, unit: { min: 0.6, max: 0.95 }, goated: { min: 0.95, max: 1.4 } },
};
const weightRedSlot: AffixSlotSpec = {
  stat: 'weightRed',
  weight: 0.5,
  rolls: { uncommon: { min: 0.1, max: 0.2 }, rare: { min: 0.2, max: 0.4 }, epic: { min: 0.4, max: 0.6 }, legendary: { min: 0.6, max: 1 }, unit: { min: 1, max: 1.5 }, goated: { min: 1.5, max: 2.2 } },
};

const ATTACK_AFFIXES = [critChanceSlot, critMultSlot, powerSlot, cooldownSlot, weightRedSlot];

export const PART_DEFS: Record<string, PartDef> = {
  // ---- The Mech Core (intrinsic, never drops; see mechCore.ts) -------------
  'src.mech_core': {
    id: 'src.mech_core', category: 'source', name: 'Mech Core',
    flavor: 'The reactor at the center of you. Upgrade it for raw Power; ascend to grow more output taps. Cannot be removed. It is, after all, you.',
    baseRarity: 'rare', weight: 0, minTier: 1, dropWeight: 0,
    // Static fallback ports; the live port count is tier-dynamic (mechCore.corePorts).
    ports: [{ id: 'o1', direction: 'out', flow: 'power', role: 'flow' }],
    source: { basePower: 10 },
    affixSlots: [], maxAffixes: MAX_AFFIX,
  },

  // ---- Auxiliary Power sources (loot — bolt-on extra Power) ----------------
  'src.spite_core': {
    id: 'src.spite_core', category: 'source', name: 'Spite Cell', flavor: 'A salvaged auxiliary cell. Generates bonus Power from pure institutional resentment. Splits like any tap.',
    baseRarity: 'common', weight: 2, minTier: 1, dropWeight: 6,
    ports: [{ id: 'out', direction: 'out', flow: 'power', role: 'flow' }],
    source: { basePower: 8 },
    affixSlots: [powerSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },
  'src.reactor': {
    id: 'src.reactor', category: 'source', name: 'Hamster-Wheel Reactor', flavor: 'A bigger auxiliary source. More Power. Do not ask about the hamster.',
    baseRarity: 'uncommon', weight: 4, minTier: 1, dropWeight: 3,
    ports: [{ id: 'out', direction: 'out', flow: 'power', role: 'flow' }],
    source: { basePower: 22 },
    affixSlots: [powerSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },

  // ---- Attacks -------------------------------------------------------------
  'atk.punch': {
    id: 'atk.punch', category: 'attack', name: 'Punch', flavor: 'Applies Power directly to the nearest problem.',
    baseRarity: 'common', weight: 3, minTier: 1, dropWeight: 5,
    ports: attackPorts(1),
    attack: { kind: 'melee', damageType: 'kinetic', baseDamage: 5, cooldownMs: 1000, baseCritChance: 0.05, baseCritMult: 1.5, flowScale: 1 },
    affixSlots: ATTACK_AFFIXES, maxAffixes: MAX_AFFIX,
  },
  'atk.kick': {
    id: 'atk.kick', category: 'attack', name: 'Roundhouse', flavor: 'Slower, meaner. Some problems require a bigger foot.',
    baseRarity: 'uncommon', weight: 4, minTier: 1, dropWeight: 4,
    ports: attackPorts(1),
    attack: { kind: 'melee', damageType: 'kinetic', baseDamage: 14, cooldownMs: 1800, baseCritChance: 0.08, baseCritMult: 1.7, flowScale: 1.6 },
    affixSlots: ATTACK_AFFIXES, maxAffixes: MAX_AFFIX,
  },
  'atk.shoot': {
    id: 'atk.shoot', category: 'attack', name: 'Sidearm', flavor: 'Fast, ranged, low commitment. The CORE approves.',
    baseRarity: 'common', weight: 3, minTier: 1, dropWeight: 5,
    ports: attackPorts(1),
    attack: { kind: 'ranged', damageType: 'kinetic', baseDamage: 3, cooldownMs: 500, baseCritChance: 0.06, baseCritMult: 1.5, flowScale: 0.6 },
    affixSlots: ATTACK_AFFIXES, maxAffixes: MAX_AFFIX,
  },
  'atk.arcbolt': {
    id: 'atk.arcbolt', category: 'attack', name: 'Arc Bolt', flavor: 'A satisfying energy lance. Rogue toasters hate it.',
    baseRarity: 'rare', weight: 5, minTier: 2, dropWeight: 3,
    ports: attackPorts(2),
    attack: { kind: 'ranged', damageType: 'energy', baseDamage: 9, cooldownMs: 900, baseCritChance: 0.1, baseCritMult: 1.8, flowScale: 1.1 },
    affixSlots: ATTACK_AFFIXES, maxAffixes: MAX_AFFIX,
  },
  'atk.sweep': {
    id: 'atk.sweep', category: 'attack', name: 'Sweep', flavor: 'Hits everything in front of you. Crowd control, comedically.',
    baseRarity: 'rare', weight: 6, minTier: 2, dropWeight: 3,
    ports: attackPorts(2),
    attack: { kind: 'aoe', damageType: 'kinetic', baseDamage: 7, cooldownMs: 1500, baseCritChance: 0.05, baseCritMult: 1.5, flowScale: 0.9, maxTargets: 30 },
    affixSlots: ATTACK_AFFIXES, maxAffixes: MAX_AFFIX,
  },
  'atk.festerbolt': {
    id: 'atk.festerbolt', category: 'attack', name: 'Festering Bolt', flavor: 'Corrosive. It keeps working after you stop caring.',
    baseRarity: 'epic', weight: 7, minTier: 3, dropWeight: 2,
    ports: attackPorts(3),
    attack: { kind: 'aoe', damageType: 'corrosive', baseDamage: 20, cooldownMs: 1300, baseCritChance: 0.12, baseCritMult: 2.0, flowScale: 1.3, maxTargets: 30 },
    affixSlots: ATTACK_AFFIXES, maxAffixes: MAX_AFFIX,
  },

  'atk.flak': {
    id: 'atk.flak', category: 'attack', name: 'Flak Burst', flavor: 'A spread of shrapnel. Indiscriminate, like all the best ideas.',
    baseRarity: 'epic', weight: 8, minTier: 3, dropWeight: 2,
    ports: attackPorts(2),
    attack: { kind: 'aoe', damageType: 'kinetic', baseDamage: 16, cooldownMs: 1100, baseCritChance: 0.1, baseCritMult: 1.9, flowScale: 1.1, maxTargets: 30 },
    affixSlots: ATTACK_AFFIXES, maxAffixes: MAX_AFFIX,
  },
  'atk.railgun': {
    id: 'atk.railgun', category: 'attack', name: 'Railgun', flavor: 'One target. A great deal of regret. Slow, devastating.',
    baseRarity: 'legendary', weight: 10, minTier: 4, dropWeight: 1,
    ports: attackPorts(3),
    attack: { kind: 'ranged', damageType: 'energy', baseDamage: 120, cooldownMs: 2600, baseCritChance: 0.2, baseCritMult: 2.5, flowScale: 2.2 },
    affixSlots: ATTACK_AFFIXES, maxAffixes: MAX_AFFIX,
  },

  // ---- Amplifiers ----------------------------------------------------------
  'amp.overclock': {
    id: 'amp.overclock', category: 'amplifier', name: 'Overclock', flavor: 'Multiplies the Power passing through. Runs hot. Worth it.',
    baseRarity: 'uncommon', weight: 3, minTier: 1, dropWeight: 4,
    ports: [{ id: 'in', direction: 'in', flow: 'power', role: 'flow' }, { id: 'out', direction: 'out', flow: 'power', role: 'flow' }],
    amplifier: { multBase: 1.5, multPerLevel: 0.15 },
    affixSlots: [powerSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },

  'amp.surge': {
    id: 'amp.surge', category: 'amplifier', name: 'Surge Capacitor', flavor: 'A bigger multiplier for a bigger weight cost. Math checks out.',
    baseRarity: 'epic', weight: 6, minTier: 3, dropWeight: 2,
    ports: [{ id: 'in', direction: 'in', flow: 'power', role: 'flow' }, { id: 'out', direction: 'out', flow: 'power', role: 'flow' }],
    amplifier: { multBase: 2.0, multPerLevel: 0.22 },
    affixSlots: [powerSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },

  // ---- Tuners (wire into attack modifier ports) ----------------------------
  'tuner.crit_rate': {
    id: 'tuner.crit_rate', category: 'tuner', name: 'Crit Gremlin', flavor: 'Raises crit chance of whatever it is wired to.',
    baseRarity: 'uncommon', weight: 2, minTier: 1, dropWeight: 4,
    ports: [{ id: 'out', direction: 'out', flow: 'mod', role: 'modifier' }],
    tuner: { stat: 'critChance', base: 0.06, perLevel: 0.02 },
    affixSlots: [critChanceSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },
  'tuner.crit_power': {
    id: 'tuner.crit_power', category: 'tuner', name: 'Big Number Enthusiast', flavor: 'Raises crit damage. Loves a big number.',
    baseRarity: 'rare', weight: 2, minTier: 1, dropWeight: 3,
    ports: [{ id: 'out', direction: 'out', flow: 'mod', role: 'modifier' }],
    tuner: { stat: 'critMult', base: 0.4, perLevel: 0.12 },
    affixSlots: [critMultSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },
  'tuner.power': {
    id: 'tuner.power', category: 'tuner', name: 'Amp Tuner', flavor: 'Flat power boost to the wired attack.',
    baseRarity: 'uncommon', weight: 2, minTier: 1, dropWeight: 4,
    ports: [{ id: 'out', direction: 'out', flow: 'mod', role: 'modifier' }],
    tuner: { stat: 'power', base: 0.25, perLevel: 0.08 },
    affixSlots: [powerSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },
  'tuner.haste': {
    id: 'tuner.haste', category: 'tuner', name: 'Haste Module', flavor: 'Reduces cooldown of the wired attack.',
    baseRarity: 'rare', weight: 3, minTier: 2, dropWeight: 3,
    ports: [{ id: 'out', direction: 'out', flow: 'mod', role: 'modifier' }],
    tuner: { stat: 'cooldown', base: 0.05, perLevel: 0.015 },
    affixSlots: [cooldownSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },

  // ---- Utility (global, by being equipped) ---------------------------------
  'util.scavenger': {
    id: 'util.scavenger', category: 'utility', name: 'Scavenger Protocol', flavor: 'More parts drop. The CORE is a hoarder.',
    baseRarity: 'rare', weight: 3, minTier: 1, dropWeight: 2,
    ports: [], utility: { stat: 'dropRate', base: 0.25, perLevel: 0.05 },
    affixSlots: [dropSlot, weightRedSlot], maxAffixes: MAX_AFFIX,
  },
  'util.magnet': {
    id: 'util.magnet', category: 'utility', name: 'Scrap Magnet', flavor: 'More Scrap per kill. Money is a construct, but a useful one.',
    baseRarity: 'uncommon', weight: 2, minTier: 1, dropWeight: 3,
    ports: [], utility: { stat: 'scrapMult', base: 0.2, perLevel: 0.05 },
    affixSlots: [weightRedSlot], maxAffixes: MAX_AFFIX,
  },
  'util.repair_gel': {
    id: 'util.repair_gel', category: 'utility', name: 'Repair Gel Pump', flavor: 'Drips self-repair nanogel while equipped. Smells like victory and ozone.',
    baseRarity: 'uncommon', weight: 3, minTier: 1, dropWeight: 3,
    ports: [], utility: { stat: 'regen', base: 0.008, perLevel: 0.002 },
    affixSlots: [weightRedSlot], maxAffixes: MAX_AFFIX,
  },
  'util.nano_swarm': {
    id: 'util.nano_swarm', category: 'utility', name: 'Nano-Swarm Hive', flavor: 'A cloud of welder-bots lives in your chassis now. They pay rent in hull plating.',
    baseRarity: 'epic', weight: 6, minTier: 3, dropWeight: 2,
    ports: [], utility: { stat: 'regen', base: 0.018, perLevel: 0.004 },
    affixSlots: [weightRedSlot, dropSlot], maxAffixes: MAX_AFFIX,
  },

  // ---- Frame parts (raise Weight capacity) ---------------------------------
  'frame.plating': {
    id: 'frame.plating', category: 'frame', name: 'Salvaged Plating', flavor: 'Bolt-on capacity. More room for bad ideas.',
    baseRarity: 'uncommon', weight: 0, minTier: 1, dropWeight: 3,
    ports: [], frame: { weightBonus: 4 },
    affixSlots: [], maxAffixes: MAX_AFFIX,
  },
};

export function getPartDef(defId: string): PartDef {
  const def = PART_DEFS[defId];
  if (!def) throw new Error(`Unknown part def: ${defId}`);
  return def;
}

/** Defs eligible to drop at a given chassis tier, with their pool weights. */
export function droppableDefs(tier: number): { ids: string[]; weights: number[] } {
  const ids: string[] = [];
  const weights: number[] = [];
  for (const def of Object.values(PART_DEFS)) {
    if (def.dropWeight > 0 && def.minTier <= tier) {
      ids.push(def.id);
      weights.push(def.dropWeight);
    }
  }
  return { ids, weights };
}
